import React, { PropsWithChildren, ReactElement } from 'react';

import { Dialog } from '@headlessui/react';
import cn from 'classnames';

import styles from './Modal.module.css';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  closeIcon?: boolean;
  title?: string | ReactElement | ReactElement[];
  subTitle?: string | ReactElement | ReactElement[];
  size?: 'small' | 'medium' | 'large';
  headerBar?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const Modal = ({
  isOpen,
  children,
  title,
  subTitle,
  className,
  style,
  size = 'large',
  headerBar = false,
  closeIcon = false,
  onClose,
  ...props
}: PropsWithChildren<ModalProps>) => {
  return (
    <Dialog className={styles.wrapper} open={isOpen} onClose={onClose}>
      <div
        data-testid="modalOverlay"
        className={styles.overlay}
        aria-hidden="true"
      />
      <Dialog.Panel
        {...props}
        className={cn(styles.modal, styles[size], className, {
          [styles.modalWithCloseIcon]: closeIcon,
          [styles.modalWithHeaderBar]: headerBar,
        })}
        style={style}
      >
        {closeIcon && onClose && (
          <Button
            className={styles.closeIcon}
            iconOnly
            variant="tertiary"
            size="small"
            onClick={onClose}
            type="button"
            aria-label="close modal"
          >
            <Icon data-testid="closeIcon" icon="timesDefault" />
          </Button>
        )}

        {(title || subTitle) && (
          <div className={cn(styles.header, { [styles.headerBar]: headerBar })}>
            {title && (
              <Dialog.Title data-testid="modal-title" className={styles.title}>
                {title}
              </Dialog.Title>
            )}
            {subTitle && (
              <Dialog.Description
                data-testid="modal-subtitle"
                className={styles.subTitle}
              >
                {subTitle}
              </Dialog.Description>
            )}
          </div>
        )}

        <div className={styles.childWrapper}>{children}</div>
      </Dialog.Panel>
    </Dialog>
  );
};

Modal.displayName = 'Modal';
